import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash, Printer } from "@phosphor-icons/react";
import { loadPrinterSettings } from "./AdminPrinters";
import { openPrintWindow } from "../lib/printWindow";

const STATUSES = ["pendiente", "preparando", "servido", "cancelado"];
const STATUS_STYLES = {
  pendiente: "bg-yellow-100 text-yellow-700 border-yellow-300",
  preparando: "bg-blue-100 text-blue-700 border-blue-300",
  servido: "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelado: "bg-red-100 text-red-700 border-red-300",
};

function buildTicketHtml(order, cfg) {
  const lines = order.items
    .map(
      (i) =>
        `<p>${i.quantity} × <b>${escape(i.name)}</b>${
          i.variant ? ` <span class="var">(${escape(i.variant)})</span>` : ""
        }<span class="amt">${(i.price * i.quantity).toFixed(2)} €</span></p>`
    )
    .join("");
  const notes = order.notes ? `<hr/><p class="note">Nota: ${escape(order.notes)}</p>` : "";
  const table = order.table_number ? `<p><b>Mesa:</b> ${escape(order.table_number)}</p>` : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>Pedido</title>
    <style>
      @page { size: 80mm auto; margin: 4mm; }
      body { font-family: -apple-system, "Segoe UI", sans-serif; font-size: 12px; margin:0; padding: 6px 8px; color: #000; }
      h1 { font-size: 16px; margin: 0 0 6px; text-align: center; }
      hr { border: none; border-top: 1px dashed #666; margin: 8px 0; }
      p { margin: 3px 0; }
      .small { font-size: 10px; color: #666; text-align: center; }
      .amt { float: right; font-variant-numeric: tabular-nums; }
      .var { color: #666; }
      .total { font-size: 15px; font-weight: bold; text-align: right; margin-top: 8px; }
      .note { font-style: italic; }
    </style></head><body>
    <h1>${escape(cfg.headerText || "")}</h1>
    <div class="small">${new Date(order.created_at).toLocaleString("es-ES")}</div>
    <hr/>
    <p><b>${escape(order.customer_name)}</b></p>
    ${table}
    <hr/>
    ${lines}
    <hr/>
    <p class="total">Total: ${order.total.toFixed(2)} €</p>
    ${notes}
    <div class="small" style="margin-top:10px">${escape(cfg.footerText || "")}</div>
    </body></html>`;
}

function escape(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function printOrder(order) {
  const cfg = loadPrinterSettings();
  const html = buildTicketHtml(order, cfg);
  const copies = Math.max(1, Math.min(5, cfg.copies || 1));
  const w = openPrintWindow(html, { features: "width=380,height=560", autoPrint: false });
  if (!w) {
    toast.error("El navegador bloquea la ventana. Permite pop-ups y reintenta.");
    return;
  }
  setTimeout(() => {
    try {
      w.focus();
      for (let i = 0; i < copies; i++) w.print();
      setTimeout(() => { try { w.close(); } catch (err) { console.debug("admin order print close:", err); } }, 400);
    } catch (err) { console.error("print failed", err); }
  }, 400);
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [tab, setTab] = useState("active"); // 'active' | 'history'
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/orders");
      setOrders(r.data);
      const cfg = loadPrinterSettings();
      if (firstLoadRef.current) {
        r.data.forEach((o) => seenIdsRef.current.add(o.id));
        firstLoadRef.current = false;
      } else if (cfg.autoPrint) {
        r.data.forEach((o) => {
          if (!seenIdsRef.current.has(o.id) && o.status === "pendiente") {
            printOrder(o);
          }
          seenIdsRef.current.add(o.id);
        });
      } else {
        r.data.forEach((o) => seenIdsRef.current.add(o.id));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status?status=${status}`);
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/orders/${confirmDel.id}`);
      toast.success("Pedido eliminado");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pendingCount = orders.filter((o) => o.status === "pendiente").length;
  const isHistory = (s) => s === "servido" || s === "cancelado";
  const visibleOrders =
    tab === "active" ? orders.filter((o) => !isHistory(o.status)) : orders.filter((o) => isHistory(o.status));
  const historyCount = orders.filter((o) => isHistory(o.status)).length;
  const activeCount = orders.length - historyCount;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="label-cap">carta digital</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Pedidos</h1>
        </div>
        <div className="text-sm text-[var(--text-secondary)]">
          {pendingCount > 0 && tab === "active" && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--primary)] text-white font-display font-bold text-xs">
              {pendingCount} pendiente{pendingCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[var(--border)]">
        <button
          onClick={() => setTab("active")}
          className={`px-4 py-2 -mb-px border-b-2 font-display font-semibold text-sm transition ${
            tab === "active"
              ? "border-[var(--primary)] text-[var(--primary)]"
              : "border-transparent text-[var(--text-secondary)] hover:text-[var(--foreground)]"
          }`}
          data-testid="orders-tab-active"
        >
          Activos <span className="ml-1 text-xs opacity-70">({activeCount})</span>
        </button>
        <button
          onClick={() => setTab("history")}
          className={`px-4 py-2 -mb-px border-b-2 font-display font-semibold text-sm transition ${
            tab === "history"
              ? "border-[var(--primary)] text-[var(--primary)]"
              : "border-transparent text-[var(--text-secondary)] hover:text-[var(--foreground)]"
          }`}
          data-testid="orders-tab-history"
        >
          Histórico <span className="ml-1 text-xs opacity-70">({historyCount})</span>
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleOrders.map((o) => (
          <div
            key={o.id}
            className="card-flat p-5 flex flex-col"
            data-testid={`order-${o.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-display font-semibold">{o.customer_name}</p>
                <p className="text-[var(--text-secondary)] text-xs">
                  {o.table_number ? `Mesa ${o.table_number} · ` : ""}
                  {new Date(o.created_at).toLocaleString("es-ES")}
                </p>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[o.status] || ""}`}
                data-testid={`order-status-badge-${o.id}`}
              >
                {o.status}
              </span>
            </div>
            <ul className="text-sm space-y-1 mb-3 flex-1">
              {o.items.map((i, idx) => (
                <li
                  key={`${i.item_id}-${idx}`}
                  className="flex justify-between text-[var(--text-secondary)]"
                >
                  <span>
                    {i.quantity}× {i.name}
                    {i.variant && <span className="text-xs"> ({i.variant})</span>}
                  </span>
                  <span className="font-mono">
                    {(i.price * i.quantity).toFixed(2)} €
                  </span>
                </li>
              ))}
            </ul>
            {o.notes && (
              <p className="text-xs text-[var(--text-secondary)] italic mb-3">
                Nota: {o.notes}
              </p>
            )}
            <div className="flex items-center justify-between border-t border-[var(--border)] pt-3">
              <span className="font-display text-xl text-[var(--primary)] font-bold">
                {o.total.toFixed(2)} €
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => printOrder(o)}
                  className="p-2 rounded-lg hover:bg-[var(--primary)] hover:text-white text-[var(--text-secondary)] transition"
                  data-testid={`print-order-${o.id}`}
                  title="Imprimir nota"
                >
                  <Printer size={16} weight="duotone" />
                </button>
                <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                  <SelectTrigger
                    className="w-36 h-9"
                    data-testid={`order-status-${o.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  onClick={() => setConfirmDel(o)}
                  className="p-2 rounded-lg hover:bg-[var(--danger)] hover:text-white text-[var(--text-secondary)]"
                  data-testid={`delete-order-${o.id}`}
                  title="Eliminar"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {visibleOrders.length === 0 && (
          <p className="text-[var(--text-secondary)] col-span-full text-center py-16">
            {tab === "active"
              ? "Aún no hay pedidos activos. La lista se actualiza automáticamente cada 15 s."
              : "No hay pedidos servidos ni cancelados en el histórico."}
          </p>
        )}
      </div>

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-sm p-6" data-testid="confirm-delete-order">
            <h2 className="font-display text-xl font-bold tracking-tight mb-2">¿Eliminar pedido?</h2>
            <p className="text-[var(--text-secondary)] text-sm mb-5">
              {confirmDel.customer_name}
              {confirmDel.table_number ? ` · Mesa ${confirmDel.table_number}` : ""}
              {" · "}
              {confirmDel.total.toFixed(2)} €
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDel(null)}
                className="px-4 py-2 rounded-lg border border-[var(--border)] font-display font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={doDelete}
                className="btn-danger px-4 py-2 rounded-lg font-display font-bold text-white"
                data-testid="confirm-delete-order-btn"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
